import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, X, Phone } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

// Valida exactamente 10 dígitos numéricos (número local México sin lada)
const validarTelefono = (tel) => /^[0-9]{10}$/.test(tel.replace(/\s/g, ''));

const ModalConfirmacion = ({
    isOpen,
    onClose,
    onConfirm,
    titulo = "Confirmación",
    mensaje = "¿Estás seguro de realizar esta acción?",
    textoConfirmar = "Confirmar",
    textoCancelar = "Cancelar",
    tipo = "danger", // 'danger', 'warning', 'info'
    // --- Teléfono ---
    showTelefono = false,          // mostrar campo de teléfono
    telefonoValue = "",             // valor controlado desde el padre
    onTelefonoChange = null,        // callback(value) cuando cambia
    maxWidthClass = "sm:max-w-md",  // ancho máximo responsivo personalizable
    checkboxText = "",              // texto para checkbox obligatorio (ej: vigencia)
}) => {
    const [telefonoInterno, setTelefonoInterno] = useState("");
    const [touched, setTouched] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [step, setStep] = useState(1);

    // Resetear al abrir/cerrar
    useEffect(() => {
        if (!isOpen) {
            setTelefonoInterno("");
            setTouched(false);
            setIsChecked(false);
            setStep(1);
        }
    }, [isOpen]);

    const telefonoCurrent = onTelefonoChange ? telefonoValue : telefonoInterno;
    const isValidTel = validarTelefono(telefonoCurrent);
    const canConfirm = (!showTelefono || isValidTel) && (!checkboxText || isChecked);

    const handleTelChange = (e) => {
        // Solo permitir dígitos, máximo 10
        const raw = e.target.value.replace(/\D/g, "").slice(0, 10);
        if (onTelefonoChange) {
            onTelefonoChange(raw);
        } else {
            setTelefonoInterno(raw);
        }
    };

    const config = {
        danger: {
            icon: <AlertCircle className="w-8 h-8 text-red-600" />,
            btnConfirm: "bg-red-600 hover:bg-red-700",
            iconBg: "bg-red-50"
        },
        warning: {
            icon: <AlertCircle className="w-8 h-8 text-amber-600" />,
            btnConfirm: "bg-amber-600 hover:bg-amber-700",
            iconBg: "bg-amber-50"
        },
        info: {
            icon: <AlertCircle className="w-8 h-8 text-blue-600" />,
            btnConfirm: "bg-blue-600 hover:bg-blue-700",
            iconBg: "bg-blue-50"
        }
    };

    const currentConfig = config[tipo] || config.info;

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[9999] overflow-y-auto">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-slate-900/75 pointer-events-auto"
                    />

                    {/* Scrollable Container Wrapper */}
                    <div className="flex min-h-full items-center justify-center p-4 sm:p-6 text-center pointer-events-none">
                        {/* Modal Content */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className={twMerge(
                                "relative bg-white w-full max-w-md rounded-[1.5rem] sm:rounded-[2.5rem] shadow-2xl overflow-hidden px-4 py-5 sm:p-8 pointer-events-auto my-4",
                                maxWidthClass
                            )}
                        >
                            {/* Close button */}
                            <button
                                onClick={onClose}
                                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-all"
                            >
                                <X className="w-5 h-5" />
                            </button>

                            <div className="flex flex-col items-center text-center gap-3 sm:gap-6">
                                {/* Icon */}
                                <div className={twMerge("hidden sm:block p-4 rounded-[1.5rem]", currentConfig.iconBg)}>
                                    {currentConfig.icon}
                                </div>

                                {/* Paso indicador */}
                                {showTelefono && (
                                    <span className="px-2.5 py-0.5 bg-slate-100 text-slate-500 rounded-full text-[9px] sm:text-[10px] font-extrabold uppercase tracking-wider">
                                        Paso {step} de 2
                                    </span>
                                )}

                                <div className="space-y-1 sm:space-y-2 w-full">
                                    <h3 className="text-lg sm:text-2xl font-black text-slate-900 tracking-tight">
                                        {step === 1 ? titulo : "Contacto y Vigencia"}
                                    </h3>
                                    {step === 1 && (
                                        <div className="text-xs sm:text-sm text-slate-500 font-medium leading-relaxed">
                                            {mensaje}
                                        </div>
                                    )}
                                </div>

                                {/* Paso 2: Teléfono y Checkbox de Vigencia */}
                                {step === 2 && showTelefono && (
                                    <div className="w-full space-y-4">
                                        {/* Campo de teléfono */}
                                        <div className="w-full text-left">
                                            <label className="block text-xs font-extrabold text-slate-600 uppercase tracking-widest mb-2">
                                                <Phone className="inline w-3.5 h-3.5 mr-1 mb-0.5" />
                                                Número de teléfono de contacto
                                            </label>
                                            <div className="flex items-center rounded-2xl border-2 overflow-hidden transition-all"
                                                style={{
                                                    borderColor: touched
                                                        ? isValidTel ? "#10b981" : "#ef4444"
                                                        : "#e2e8f0"
                                                }}
                                            >
                                                {/* Lada México */}
                                                <div className="flex items-center gap-1 px-2.5 sm:px-3 py-3 bg-slate-100 border-r-2 border-slate-200 shrink-0">
                                                    <span className="text-base leading-none">🇲🇽</span>
                                                    <span className="text-sm font-extrabold text-slate-700">+52</span>
                                                </div>
                                                <input
                                                    id="modal-telefono-input"
                                                    type="tel"
                                                    inputMode="numeric"
                                                    placeholder="3312345678"
                                                    value={telefonoCurrent}
                                                    onChange={handleTelChange}
                                                    onBlur={() => setTouched(true)}
                                                    maxLength={10}
                                                    className="flex-1 min-w-0 px-2.5 sm:px-3 py-3 text-base sm:text-sm font-bold text-slate-800 bg-white outline-none placeholder:text-slate-300 tracking-wider sm:tracking-widest"
                                                />
                                                {/* Indicador de validez */}
                                                <div className="pl-1 pr-2.5 sm:pr-3 shrink-0">
                                                    {touched && (
                                                        isValidTel
                                                            ? <span className="text-emerald-500 font-bold text-lg">✓</span>
                                                            : <span className="text-red-400 font-bold text-lg">✗</span>
                                                    )}
                                                </div>
                                            </div>
                                            {/* Mensajes de error/ayuda */}
                                            <div className="mt-1.5 min-h-[18px]">
                                                {touched && !isValidTel && (
                                                    <p className="text-[11px] text-red-500 font-bold">
                                                        {telefonoCurrent.length === 0
                                                            ? "El número de teléfono es obligatorio."
                                                            : `Ingresa exactamente 10 dígitos (llevas ${telefonoCurrent.length}).`}
                                                    </p>
                                                )}
                                                {isValidTel && (
                                                    <p className="text-[11px] text-emerald-600 font-bold">
                                                        ✓ Número válido: +52 {telefonoCurrent}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Checkbox obligatorio */}
                                        {checkboxText && (
                                            <label className="w-full flex items-start gap-3 cursor-pointer select-none bg-slate-50 hover:bg-slate-100/70 p-3.5 rounded-2xl border border-slate-200/50 transition-all text-left">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={(e) => setIsChecked(e.target.checked)}
                                                    className="mt-0.5 w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500 cursor-pointer animate-pulse"
                                                />
                                                <span className="text-[11px] font-semibold text-slate-600 leading-normal">
                                                    {checkboxText}
                                                </span>
                                            </label>
                                        )}
                                    </div>
                                )}

                                {/* Footer de Botones */}
                                <div className="flex flex-col sm:flex-row gap-3 w-full mt-2">
                                    {step === 1 ? (
                                        <>
                                            <button
                                                type="button"
                                                onClick={onClose}
                                                className="flex-1 px-6 py-4 bg-slate-100 text-slate-600 rounded-[1.25rem] font-bold text-sm uppercase tracking-widest hover:bg-slate-200 transition-all active:scale-95"
                                            >
                                                {textoCancelar}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (showTelefono) {
                                                        setStep(2);
                                                    } else {
                                                        onConfirm();
                                                        onClose();
                                                    }
                                                }}
                                                className={twMerge(
                                                    "flex-1 px-6 py-4 text-white rounded-[1.25rem] font-bold text-sm uppercase tracking-widest transition-all active:scale-95",
                                                    currentConfig.btnConfirm
                                                )}
                                            >
                                                {showTelefono ? "Siguiente" : textoConfirmar}
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => setStep(1)}
                                                className="flex-1 px-6 py-4 bg-slate-100 text-slate-600 rounded-[1.25rem] font-bold text-sm uppercase tracking-widest hover:bg-slate-200 transition-all active:scale-95"
                                            >
                                                Atrás
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (!canConfirm) {
                                                        setTouched(true);
                                                        return;
                                                    }
                                                    onConfirm();
                                                    onClose();
                                                }}
                                                disabled={!canConfirm}
                                                className={twMerge(
                                                    "flex-1 px-6 py-4 text-white rounded-[1.25rem] font-bold text-sm uppercase tracking-widest transition-all active:scale-95",
                                                    canConfirm
                                                        ? currentConfig.btnConfirm
                                                        : "bg-slate-300 cursor-not-allowed"
                                                )}
                                            >
                                                {textoConfirmar}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default ModalConfirmacion;
